import { Link } from "react-router-dom";
import Breadcrumb from "@/components/video/Breadcrumb";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const highlights = [
  {
    icon: "ri-eye-line",
    title: "内容预览清晰",
    desc: "以缩略图、标题、分类标签与简介结构化展示视频信息，帮助你快速判断是否感兴趣。",
  },
  {
    icon: "ri-filter-3-line",
    title: "筛选与排序",
    desc: "按年份、地区筛选视频资源，并支持按最新发布或热门程度排序，找片更高效。",
  },
  {
    icon: "ri-device-line",
    title: "多端友好",
    desc: "官网同时适配 PC 与手机浏览器，手机上同样拥有顺畅的浏览与点击体验。",
  },
  {
    icon: "ri-information-line",
    title: "演示透明",
    desc: "播放为前端演示效果，下载为示范链接，官网会明确标注，不做夸大宣传。",
  },
];

const guidelines = [
  "请通过官方网站提供的渠道下载 App，避免使用来源不明的安装包。",
  "站内视频封面与简介均为演示用途，不代表真实可播放内容。",
  "请尊重内容版权，不传播、不分享任何未经授权的内容。",
  "如对内容或功能有任何疑问，欢迎通过下方联系方式与我们沟通。",
];

export default function About() {
  useDocumentTitle(
    "关于我们 - 黄瓜视频App官方网站",
    "了解黄瓜视频App官方网站的品牌定位、网站特色与联系方式，我们致力于提供清晰、便捷的高清视频内容展示与下载引导。",
    "关于我们,黄瓜视频App官网,视频平台介绍,联系方式",
  );

  return (
    <div className="w-full pt-20 md:pt-24">
      <section className="w-full px-4 md:px-8 pt-8 md:pt-12 pb-10 border-b border-background-200/60">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
        <p className="mt-5 text-sm font-label text-primary-400 tracking-wide">About Us</p>
        <h1 className="mt-2 text-3xl md:text-5xl font-heading font-bold text-foreground-950 leading-tight">
          关于黄瓜视频App官方网站
        </h1>
        <p className="mt-5 text-sm md:text-base text-foreground-400 max-w-3xl leading-relaxed">
          黄瓜视频App官方网站是一站式高清视频资源展示与软件下载平台。我们希望用清晰的内容组织和简洁的浏览体验，
          让每一位用户都能快速找到感兴趣的视频、了解 App 特色，并顺利完成下载安装。
        </p>
      </section>

      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          <div className="lg:col-span-2 space-y-5">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">品牌与定位</h2>
            <p className="text-sm md:text-base text-foreground-400 leading-relaxed">
              我们面向热爱观看影视剧、短视频并追求高清画质视频的普通用户。网站汇集电影、电视剧与短视频三大类内容预览，
              以现代门户风格的界面，提供清爽的浏览路径：从首页焦点推荐到分类筛选，再到详情页预览，层层递进、重点突出。
            </p>
            <p className="text-sm md:text-base text-foreground-400 leading-relaxed">
              需要说明的是，本站为展示型官方网站，不含用户登录、注册与支付功能；视频播放按钮用于功能演示，
              不会加载真实视频流；App 下载链接当前为示范地址，正式上线前可替换为真实地址。
            </p>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 pt-4">用户守则</h2>
            <ul className="space-y-3">
              {guidelines.map((g) => (
                <li key={g} className="flex items-start gap-3 text-sm md:text-base text-foreground-400 leading-relaxed">
                  <i className="ri-checkbox-circle-line w-5 h-5 flex items-center justify-center text-accent-400 shrink-0 mt-0.5" />
                  {g}
                </li>
              ))}
            </ul>
          </div>

          <aside className="space-y-5">
            <div className="p-6 rounded-lg bg-background-100 border border-background-200/60">
              <h2 className="text-lg font-heading font-bold text-foreground-950">联系方式</h2>
              <ul className="mt-5 space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <i className="ri-mail-line w-5 h-5 flex items-center justify-center text-primary-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-foreground-500 text-xs">合作与反馈邮箱</p>
                    <a
                      href="mailto:support@huanggua-video.example.com"
                      className="text-foreground-900 hover:text-primary-400 transition-colors break-all"
                    >
                      support@huanggua-video.example.com
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-customer-service-2-line w-5 h-5 flex items-center justify-center text-primary-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-foreground-500 text-xs">客服电话</p>
                    <a href="tel:4000000000" className="text-foreground-900 hover:text-primary-400 transition-colors">
                      400-000-0000
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-time-line w-5 h-5 flex items-center justify-center text-primary-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-foreground-500 text-xs">服务时间</p>
                    <p className="text-foreground-900">每日 09:00 - 21:00</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="p-6 rounded-lg bg-primary-950 border border-primary-900/60">
              <h2 className="text-lg font-heading font-bold text-foreground-900">立即体验</h2>
              <p className="mt-3 text-sm text-foreground-300 leading-relaxed">
                下载黄瓜视频App，体验高清视频播放与离线下载。
              </p>
              <Link
                to="/download"
                className="mt-5 inline-flex items-center justify-center gap-2 w-full h-11 rounded-md bg-primary-500 hover:bg-primary-600 text-background-950 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-download-2-line w-5 h-5 flex items-center justify-center" />
                前往下载页
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-100">
        <div className="mb-10">
          <p className="text-sm font-label text-accent-400 tracking-wide">Highlights</p>
          <h2 className="mt-1 text-2xl md:text-3xl font-heading font-bold text-foreground-950">网站特色</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
          {highlights.map((h) => (
            <div key={h.title} className="flex gap-4 p-5 rounded-lg bg-background-50 border border-background-200/60">
              <span className="w-11 h-11 rounded-lg bg-primary-500/15 text-primary-400 flex items-center justify-center shrink-0">
                <i className={`${h.icon} text-xl`} />
              </span>
              <div>
                <h3 className="text-base font-heading font-semibold text-foreground-950">{h.title}</h3>
                <p className="mt-2 text-sm text-foreground-400 leading-relaxed">{h.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}